import { ThemeProvider } from "@mui/material";
import type { FunctionComponent, PartialPropsOf, ReactNode } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import type { Required } from "utility-types";

import { ArticleCommentsDrawer } from "~/components/article/article-comments-drawer";
import { ArticleImageGallery } from "~/components/article/article-image-gallery";
import { ImageSize } from "~/components/article/article-leading/article-leading-images/enums";
import { ArticleLinkingData } from "~/components/article/article-linking-data";
import { usePaywallCheckerContext } from "~/components/article/article-paywall-checker/contexts";
import { article as articleData } from "~/data";
import { useArticleUtils } from "~/lib/hooks/article-utils";
import type { articleRenderArticle$key } from "~/queries/__generated__/articleRenderArticle.graphql";

import { GenericArticle } from "./generic-article";
import { usePianoIntegration } from "./hooks";
import { useArticleBaseAdvertisingInfo } from "./hooks/article-base-advertising-info";
import { MorningStudioArticle } from "./morning-studio-article";
import { NewsletterArticle } from "./newsletter-article";
import { OpinionArticle } from "./opinion-article";
import {
  ArticleRenderContainer,
  PianoTrackerNode,
  StyledAdSlot,
  StyledArticleActionWidget,
  StyledArticleAuthorBlock,
  StyledArticleAuthorsDetails,
  StyledArticleCommentsTrigger,
  StyledArticleLeading,
  StyledArticleLeftHandSideWidgets,
  StyledArticleMoreOnThisList,
  StyledArticlePaywallContainer,
  StyledArticleSections,
  StyledArticleShareWidget,
  StyledArticleSponsor,
  StyledArticleTopicFollowWidget,
  StyledArticleTopics,
  StyledArticleVocabBox,
  StyledContentBody,
  StyledContentHeadline,
  StyledContentSubHeadline,
} from "./styles";
import { theme as defaultTheme } from "./theme";

export * from "./types";

export type Props = {
  reference: articleRenderArticle$key;
};

export type DefaultComponentsProvider = {
  actionWidget: (props?: PartialPropsOf<typeof StyledArticleActionWidget>) => ReactNode;
  adslot: (props: Required<PartialPropsOf<typeof StyledAdSlot>, "sizes" | "adUnit">) => ReactNode;
  authorBlock: (props?: PartialPropsOf<typeof StyledArticleAuthorBlock>) => ReactNode;
  authorDetails: (props?: PartialPropsOf<typeof StyledArticleAuthorsDetails>) => ReactNode;
  body: (props?: PartialPropsOf<typeof StyledContentBody>) => ReactNode;
  commentTrigger: (props?: PartialPropsOf<typeof StyledArticleCommentsTrigger>) => ReactNode;
  headline: (props?: PartialPropsOf<typeof StyledContentHeadline>) => ReactNode;
  leading: (props?: PartialPropsOf<typeof StyledArticleLeading>) => ReactNode;
  leftHandSideWidgets: (
    props: Required<PartialPropsOf<typeof StyledArticleLeftHandSideWidgets>, "topPicksWidgetProps">,
  ) => ReactNode;
  moreOnThis: (props?: PartialPropsOf<typeof StyledArticleMoreOnThisList>) => ReactNode;
  sections: (props?: PartialPropsOf<typeof StyledArticleSections>) => ReactNode;
  shareWidget: (props?: PartialPropsOf<typeof StyledArticleShareWidget>) => ReactNode;
  sponsor: (props?: PartialPropsOf<typeof StyledArticleSponsor>) => ReactNode;
  subHeadline: (props?: PartialPropsOf<typeof StyledContentSubHeadline>) => ReactNode;
  topicFollowWidget: (props?: PartialPropsOf<typeof StyledArticleTopicFollowWidget>) => ReactNode;
  topics: (props?: PartialPropsOf<typeof StyledArticleTopics>) => ReactNode;
  vocabBox: (props?: PartialPropsOf<typeof StyledArticleVocabBox>) => ReactNode;
};

export const ArticleRender: FunctionComponent<Props> = ({ reference: reference_ }) => {
  const article = useFragment(
    graphql`
      fragment articleRenderArticle on Article
      @argumentDefinitions(customContents: { type: "[CustomContentInput]", defaultValue: [] }) {
        entityId
        ...articleActionWidgetArticle
        ...articleAuthorBlockArticle
        ...articleAuthorDetailsContent
        ...articleBaseAdvertisingInfoArticle
        ...articleCommentsTriggerArticle
        ...articleImageGalleryContent
        ...articleLeadingArticle
        ...articleLeftHandSideWidgetsContent
        ...articleLinkingDataContent
        ...articleMoreOnThisListArticle
        ...articleSectionsContent
        ...articleShareWidgetArticle
        ...articleSponsorContent
        ...articleTopicFollowWidgetArticle
        ...articleTopicsContent
        ...articleVocabBoxArticle
        ...contentBodyContent @arguments(customContents: $customContents)
        ...contentHeadlineContent
        ...contentSchemaRenderContent
        ...contentSubHeadlineContent
        ...morningStudioArticleContent
        ...newsletterArticleArticle
        ...pianoIntegrationArticle
        ...articleUtilsArticle
        ...opinionArticleContent
      }
    `,
    reference_,
  );

  const advertising = useArticleBaseAdvertisingInfo(article);
  const { triggerElement } = usePianoIntegration(article);
  const { getArticleType } = useArticleUtils();

  const { paywallMap } = usePaywallCheckerContext();
  const isShowInlinePaywall = useMemo(
    () => paywallMap?.[article.entityId],
    [article.entityId, paywallMap],
  );

  const provider: DefaultComponentsProvider = {
    actionWidget: props => <StyledArticleActionWidget reference={article} {...props} />,
    adslot: props => <StyledAdSlot {...advertising} {...props} />,
    authorBlock: props => <StyledArticleAuthorBlock reference={article} {...props} />,
    authorDetails: props => <StyledArticleAuthorsDetails reference={article} {...props} />,
    body: props => (
      <>
        <StyledContentBody
          advertising={advertising}
          isShowInlinePaywall={isShowInlinePaywall}
          reference={article}
          {...props}
        />
        <StyledArticlePaywallContainer isShowInlinePaywall={isShowInlinePaywall} {...props} />
      </>
    ),
    commentTrigger: props => (
      <StyledArticleCommentsTrigger
        location="Article Bottom"
        reference={article}
        variant="banner"
        {...props}
      />
    ),
    headline: props => <StyledContentHeadline reference={article} {...props} />,
    leading: props => (
      <StyledArticleLeading
        {...props}
        reference={article}
        responsiveVariants={{
          desktopUp: ImageSize["1200x800"],
          mobileUp: ImageSize["768x768"],
          tabletUp: ImageSize["768x768"],
        }}
      />
    ),
    leftHandSideWidgets: props => (
      <StyledArticleLeftHandSideWidgets reference={article} {...props} />
    ),
    moreOnThis: props => <StyledArticleMoreOnThisList reference={article} {...props} />,
    sections: props => <StyledArticleSections reference={article} {...props} />,
    shareWidget: props => <StyledArticleShareWidget reference={article} {...props} />,
    sponsor: props => <StyledArticleSponsor reference={article} {...props} />,
    subHeadline: props => <StyledContentSubHeadline reference={article} {...props} />,
    topicFollowWidget: props => <StyledArticleTopicFollowWidget reference={article} {...props} />,
    topics: props => <StyledArticleTopics reference={article} {...props} />,
    vocabBox: props => <StyledArticleVocabBox content={article} {...props} />,
  };

  const handleRenderArticle = () => {
    switch (getArticleType(article)) {
      case articleData.types.opinion.entityId:
      case articleData.types.column.entityId:
        return (
          <OpinionArticle
            isShowInlinePaywall={isShowInlinePaywall}
            provider={provider}
            reference={article}
          />
        );
      case articleData.types.studio.entityId:
        return <MorningStudioArticle provider={provider} reference={article} />;
      case articleData.types.newsletter.entityId:
        return <NewsletterArticle content={article} provider={provider} />;
      default:
        return <GenericArticle isShowInlinePaywall={isShowInlinePaywall} provider={provider} />;
    }
  };

  return (
    <ArticleRenderContainer>
      <ThemeProvider theme={defaultTheme}>
        <ArticleImageGallery reference={article}>
          <PianoTrackerNode ref={triggerElement}>{handleRenderArticle()}</PianoTrackerNode>
        </ArticleImageGallery>
        <ArticleCommentsDrawer />
      </ThemeProvider>
      <ArticleLinkingData reference={article} />
    </ArticleRenderContainer>
  );
};

ArticleRender.displayName = "ArticleRender";
